// Wrong-answer ledger — centralizes the write-side logic for recording quiz
// and mock-exam attempt results into Progress.wrongAnswers.
//
// Extracted from quiz-view.tsx (persistSessionResults) and mock-exam-view.tsx
// (submit) which had structurally identical ledger-update blocks.

import type { WrongAnswerEntry } from '@examforge/shared/schemas';

/**
 * Update (or create) a wrong-answer ledger entry for a single attempt.
 *
 * Rules:
 * - Correct answer: increment correctCount + bump lastSeen on an existing
 *   entry.  No entry is created for a first-time correct answer (it wouldn't
 *   be "wrong").
 * - Wrong answer: increment wrongCount + set lastWrong on an existing entry;
 *   create a fresh entry with wrongCount=1 if it's the first miss.
 *
 * Returns the updated entry, or undefined if no entry should be stored (i.e.
 * first-time correct answer — nothing to track).
 */
export function recordWrongAnswerEntry(
  existing: WrongAnswerEntry | undefined,
  isCorrect: boolean,
  itemType: 'mcq' | 'tf',
  itemCaseId: string | null,
  finishedAt: string,
): WrongAnswerEntry | undefined {
  if (isCorrect) {
    if (existing) {
      return {
        ...existing,
        correctCount: existing.correctCount + 1,
        lastSeen: finishedAt,
      };
    }
    // First-time correct — no entry needed.
    return undefined;
  }

  // Wrong answer.
  if (existing) {
    return {
      ...existing,
      wrongCount: existing.wrongCount + 1,
      lastWrong: finishedAt,
      lastSeen: finishedAt,
    };
  }

  // First-time wrong — create entry.
  return {
    type: itemType,
    caseId: itemCaseId,
    wrongCount: 1,
    correctCount: 0,
    lastWrong: finishedAt,
    lastSeen: finishedAt,
  };
}
